import React, { ChangeEvent, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import clsx from 'clsx';
import { MuiTelInputInfo } from 'mui-tel-input';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  CarBrandList,
  CarModelList,
  CarSubTypeList,
  CarType,
  CarTypeList,
} from 'entities/car';
import { apiClient } from 'shared/api';
import { useUser } from 'shared/api/tanstack';
import CloseIcon from 'shared/assets/icons/close.svg';
import { useFormAutoSave } from 'shared/hooks/useFormAutoSave';
import { validateINN, ValidationRegular } from 'shared/model/constants/validations';
import { ButtonCustom } from 'shared/ui/button-custom';
import { CheckboxInput } from 'shared/ui/checkbox-input';
import { FileInputCustom } from 'shared/ui/file-input-custom';
import { PhoneInput } from 'shared/ui/phone-input/phone-input';
import { SelectInput } from 'shared/ui/select-input';
import { SliderInput } from 'shared/ui/slider-input';
import { TextInput } from 'shared/ui/text-input';
import styles from './main-leasing-calc.module.scss';

interface LeasingCalcProps {
  title: string;
  isDashboard?: boolean;
}

export const MainLeasingCalc = (props: LeasingCalcProps) => {
  const { title, isDashboard } = props;
  const [searchParams] = useSearchParams();
  const [titleModalBtn, setTitleModalBtn] = useState<string>('Войти в личный кабинет');
  const [url, setUrl] = useState<string>('/auth');

  const [open, setOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState('');
  const [modalDesc, setModalDesc] = React.useState('');

  const [kp, setKp] = useState<any>();

  const [inn, setInn] = useState<string | number>('');
  const [bio, setBio] = useState<string | number>('');
  const [email, setEmail] = useState<string | number>('');
  const [phone, setPhone] = useState<string | undefined>('');

  const [hasRequiredFields, setHasRequiredFields] = useState(false);

  useEffect(() => {
    setHasRequiredFields(!!inn && !!phone && !!email && !!bio);
  }, [inn, phone, email, bio]);

  const [agentInn, setAgentInn] = useState<string | number>('');
  const [agentReward, setAgentReward] = useState<string | number>('');
  const [agentBio, setAgentBio] = useState<string | number>('');
  const [agentEmail, setAgentEmail] = useState<string | number>('');
  const [agentPhone, setAgentPhone] = useState<string | number>('');

  const [quantity, setQuantity] = useState<string | number>('');
  const [revenue, setRevenue] = useState<string | number>('');

  const [check, setCheck] = useState(false);
  const [checkErr, setCheckErr] = useState(false);
  const handleChangeCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheck(e.target.checked);
    e.target.checked ? setCheckErr(false) : setCheckErr(true);
  };
  const rules = (event: any) => {
    event?.preventDefault();
    const link = document.createElement('a');
    link.href = '/Политика_в_отношении_обработки_персональных_данных.pdf';
    link.download = 'Политика в отношении обработки персональных данных.pdf';
    link.click();
  };

  const handleChangeInn = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInn(value || '');
    const validInn = validateINN(value);

    setInnErr(!validInn);
  };
  const handleChangeBio = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setBio(value);
    const bio_valid: boolean = !(
      value?.toString().length && ValidationRegular.BIO.test(value?.toString())
    );

    setBioErr(bio_valid);
  };
  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    const email_valid: boolean = !(
      value?.toString().length && ValidationRegular.MAIL.test(value?.toString())
    );
    setEmailErr(email_valid);
  };

  const handleChangePhone = (value: string, info: MuiTelInputInfo) => {
    let formatted = value.replace(/[^\d+]/g, '');
    if (formatted.startsWith('8')) {
      formatted = '+7' + formatted.slice(1);
    }
    if (!formatted.startsWith('+7')) {
      formatted = '+7';
    }
    const digitsOnly = formatted.replace(/\D/g, '');
    if (digitsOnly.length > 11) {
      formatted = '+' + digitsOnly.slice(0, 11);
    }
    setPhone(formatted);
    const isValid = /^\+7\d{10}$/.test(formatted);
    setPhoneErr(!isValid);
  };
  const handleChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuantity(value);
    const quantity_valid: boolean = !(
      value?.toString().length && ValidationRegular.QUANTITY.test(value?.toString())
    );

    setQuantityErr(quantity_valid);
  };
  const handleChangeRevenue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRevenue(value || '');
    const revenue_valid: boolean = !ValidationRegular.REVENUE.test(value?.toString());

    setRevenueErr(revenue_valid);
  };

  const handleChangeCurrency = (event: SelectChangeEvent<unknown>) => {
    setCurrency(event.target.value as string);
    setCurrencyErr(false);
  };

  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
  };

  const marksPrice = [
    {
      value: 1000000,
      label: '1 млн',
    },
    {
      value: 25500000,
      label: '25.5 млн',
    },
    {
      value: 50000000,
      label: '50 млн',
    },
  ];
  const marksInitialPayment = [
    {
      value: 0,
      label: '0 %',
    },
    {
      value: 24.5,
      label: '24.5 %',
    },
    {
      value: 49,
      label: '49 %',
    },
  ];
  const marksTerm = [
    {
      value: 12,
      label: '12 мес',
    },
    {
      value: 35.5,
      label: '35.5 мес',
    },
    {
      value: 84,
      label: '84 мес',
    },
  ];

  const getIdByName = (name: string | undefined, items: any) => {
    const item = items.find((item: any) => item.name === name);
    return item ? item.id : null;
  };
  const getNameById = (id: number | string, items: any) => {
    const item = items.find((item: any) => item.id === id);
    return item ? item.name : null;
  };

  const [isAgent, setIsAgent] = useState<boolean>();

  const [brandValueList, setBrandValueList] = useState<CarBrandList>([]);
  const [typeValueList, setTypeValueList] = useState<CarTypeList>([]);
  const [modelValueList, setModelValueList] = useState<CarModelList>([]);
  const [vehicleParamsList, setVehicleParamsList] = useState([]);
  const [vehicleParamsMainList, setVehicleParamsMainList] = useState<any>([]);

  const { data: user } = useUser();

  const userGetInfo = async () => {
    if (user?.user_type === 'client') {
      setBio(user.bio);
      setEmail(user.email);
      setPhone(user.phone);
      setInn(user.inn);
    } else if (user?.user_type === 'agent') {
      setIsAgent(true);
    }
  };

  // Функция для загрузки сохраненных данных
  const loadSavedData = () => {
    const savedData = getStoredData();
    if (savedData) {
      // eslint-disable-next-line no-console
      // console.log('Загружаем сохраненные данные формы лизинга:', savedData);

      if (savedData.bio) setBio(savedData.bio);
      if (savedData.email) setEmail(savedData.email);
      if (savedData.phone) setPhone(savedData.phone);
      if (savedData.inn) setInn(savedData.inn);
      if (savedData.quantity) setQuantity(savedData.quantity);
      if (savedData.revenue) setRevenue(savedData.revenue);

      // Загружаем автомобильные данные
      if (savedData.car_type && !savedData.car_type_other) {
        setCar_type(savedData.car_type);
      }
      if (savedData.car_type_other) {
        showCarTypeOtherBlock(true);
        setOtherCarType(savedData.car_type_other);
      }
      if (savedData.car_subtype && !savedData.car_subtype_other) {
        setCar_subtype(savedData.car_subtype);
      }
      if (savedData.car_subtype_other) {
        showCarSubtypeOtherBlock(true);
        setOtherSubType(savedData.car_subtype_other);
      }
      if (savedData.car_brand && !savedData.car_brand_other) {
        setBrand(savedData.car_brand);
      }
      if (savedData.car_brand_other) {
        showCarBrandOtherBlock(true);
        setOtherBrand(savedData.car_brand_other);
      }
      if (savedData.vehicle && !savedData.car_model_other) {
        setModel(savedData.vehicle);
      }
      if (savedData.car_model_other) {
        showCarModelOtherBlock(true);
        setOtherModel(savedData.car_model_other);
      }

      // Загружаем параметры лизинга
      if (savedData.payment_type) {
        setPaymentType(savedData.payment_type);
      }
      if (savedData.payment_type_other) {
        setPaymentTypeOtherBlock(true);
        setPaymentTypeOther(savedData.payment_type_other);
      }
      if (savedData.currency) {
        setCurrency(savedData.currency);
      }
    }
  };

  // useEffect(() => {
  //   userGetInfo();
  // }, [user]);

  useEffect(() => {
    if (user?.id) {
      userGetInfo();
    } else {
      loadSavedData();
    }
  }, [user]);

  const fetchCarTypesData = async () => {
    try {
      const response1 = await apiClient.get('/car-types/all');
      const data1 = await response1;
      setTypeValueList([
        ...(data1.data.data as CarTypeList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);

      if (car_type) {
        const typeId = getIdByName(car_type.toString(), data1.data.data);
        if (typeId) {
          const response2 = await apiClient.post('/car-subtypes/subtypes', {
            typeId: typeId,
          });
          const data2 = await response2;
          setSubTypeValueList([
            ...(data2.data.data as CarSubTypeList),
            { id: 0, name: 'Иное', value: 'other' },
          ]);
        }
      }
    } catch (error) {
      setTypeValueList([{ id: 0, name: 'Иное', value: 'other' }]);
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  useEffect(() => {
    fetchCarTypesData();
  }, []);

  const getBrandsForType = async (car_type: number | string) => {
    try {
      const response = await apiClient.post('/car-brands/type', { typeId: car_type });
      const data = await response;
      setBrandValueList([
        ...(data.data.data as CarBrandList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getVehicleForBrand = async (brand: number | string, type: number | string) => {
    try {
      const data = { brandId: brand, typeId: type };
      const response = await apiClient.post('/vehicles/brandType', data);
      setModelValueList(response?.data?.data);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getVehicleParams = async (model: number | string) => {
    try {
      const data = { modelId: model };
      const response = await apiClient.post('/vehicles/car_params', data);
      setVehicleParamsList(response?.data?.data);
      setVehicleParamsMainList(response?.data?.data);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getVehicleSubtype = async (model: number | string) => {
    try {
      const data = { modelId: model };
      const response = await apiClient.post('/vehicles/subtype', data);
      setSubtypeMain(response?.data?.data);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const [brand, setBrand] = useState<number | string>('');
  const [model, setModel] = useState<number | string>('');

  const [bioErr, setBioErr] = useState(false);
  const [initialErr, setInitialErr] = useState(false);
  const [leasePeriodErr, setLeasePeriodErr] = useState(false);
  const [innErr, setInnErr] = useState(false);
  const [emailErr, setEmailErr] = useState(false);
  const [phoneErr, setPhoneErr] = useState(false);
  const [priceErr, setPriceErr] = useState(false);
  const [quantityErr, setQuantityErr] = useState(false);
  const [revenueErr, setRevenueErr] = useState(false);
  const [car_brandErr, setCar_brandErr] = useState(false);
  const [car_modelErr, setCar_modelErr] = useState(false);
  const [currency, setCurrency] = useState<string>('');
  const [currencyErr, setCurrencyErr] = useState(false);
  const [validation, setValidation] = useState(false);

  const [loading, setLoading] = useState(false);

  const [car_type, setCar_type] = useState<number | string>('');
  const [car_typeErr, setCar_typeErr] = useState(false);
  const [carTypeOtherBlock, showCarTypeOtherBlock] = useState(false);
  const [otherCarType, setOtherCarType] = useState('');
  const [carTypeOtherBlockErr, setCarTypeOtherBlockErr] = useState(false);
  const handleChangeOtherCarType = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherCarType(event.target.value);
  };
  const handleChangeCarType = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const carType = typeValueList.find((item) => item.id === value);
    setCar_type(value as number);
    setBrand('');
    setModel('');
    setCar_subtype('');
    setCar_typeErr(false);
    setCar_subtypeErr(false);

    if (carType?.value === 'other') {
      showCarTypeOtherBlock(true);
    } else {
      setOtherCarType('');
      showCarTypeOtherBlock(false);
      getBrandsForType(value as number);
      getSubtypeForType(value as number);
    }
  };
  const CarTypeBlock = () => {
    return (
      <Box className={`${styles.ts} ${styles.small}`}>
        <SelectInput
          name={'car_type'}
          value={car_type}
          required
          error={car_typeErr}
          onChange={handleChangeCarType}
          placeholder={'Выберите тип ТС'}
        >
          {typeValueList?.map((item: { id: number; name: string; value: string }) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const [subtypeMain, setSubtypeMain] = useState<number | string>('');
  const [subTypeValueList, setSubTypeValueList] = useState<CarSubTypeList>([]);
  const [car_subtype, setCar_subtype] = useState<number | string>('');
  const [car_subtypeErr, setCar_subtypeErr] = useState(false);
  const [carSubtypeOtherBlock, showCarSubtypeOtherBlock] = useState(false);
  const [otherSubType, setOtherSubType] = useState('');
  const [carSubtypeOtherBlockErr, setCarSubtypeOtherBlockErr] = useState(false);
  const getSubtypeForType = async (car_type: number | string) => {
    const response = await apiClient.post('/car-subtypes/subtypes', {
      typeId: car_type,
    });
    const data = await response;
    setSubTypeValueList([
      ...(data.data.data as CarSubTypeList),
      { id: 0, name: 'Иное', value: 'other' },
    ]);
  };
  const handleChangeOtherSubType = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherSubType(event.target.value);
  };
  const handleChangeCarSubtype = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const carSubtype = subTypeValueList?.find((item) => item.id === value);

    setCar_subtype(value as number);
    setBrand('');
    setModel('');
    setCar_subtypeErr(false);

    if (carSubtype?.value === 'other') {
      showCarSubtypeOtherBlock(true);
    } else {
      setOtherSubType('');
      showCarSubtypeOtherBlock(false);
      getBrandsForSubType(value as number);
    }
  };
  const CarSubTypeBlock = () => {
    return (
      <Box className={`${styles.ts} ${styles.small}`}>
        <SelectInput
          value={car_subtype}
          name={'car_subtype'}
          onChange={handleChangeCarSubtype}
          placeholder={'Выберите подтип ТС'}
          disabled={conditionBrandDisable()}
          required
          error={car_subtypeErr}
        >
          {subTypeValueList.map((item: { id: number; name: string }) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const [carBrandOtherBlock, showCarBrandOtherBlock] = useState(false);
  const [otherBrand, setOtherBrand] = useState('');
  const [carBrandOtherBlockErr, setCarBrandOtherBlockErr] = useState(false);
  const handleChangeOtherBrand = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherBrand(event.target.value);
  };
  const getBrandsForSubType = async (car_subtype: number | string) => {
    const response = await apiClient.post('/car-brands/subtype', { subtypeId: car_subtype });
    const data = await response;
    setBrandValueList([
      ...(data.data.data as CarBrandList),
      { id: 0, name: 'Иное', value: 'other' },
    ]);
  };
  const handleChangeBrand = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const brand = brandValueList.find((item) => item.id === value);
    setBrand(event.target.value as number);
    setModel('');
    setCar_brandErr(false);

    if (brand?.value === 'other') {
      showCarBrandOtherBlock(true);
    } else {
      setOtherBrand('');
      showCarBrandOtherBlock(false);
      getVehicleForBrandAndSubType(value as number, car_subtype as number);
    }

    // if (!typeCars && !isDashboard) {
    //   getVehicleForBrand(event.target.value as number, car_type as number);
    // } else {
    //   getVehicleForBrandAndSubType(event.target.value as number, car_subtype as number);
    // }
  };
  const CarBrandOtherBlock = () => {
    return (
      <Box className={`${styles.brand} ${styles.small}`}>
        <SelectInput
          disabled={conditionBrandDisable()}
          value={brand}
          required
          error={car_brandErr}
          onChange={handleChangeBrand}
          placeholder={'Выберите марку'}
        >
          {brandValueList.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const [carModelOtherBlock, showCarModelOtherBlock] = useState(false);
  const [otherModel, setOtherModel] = useState('');
  const [carModelOtherBlockErr, setCarModelOtherBlockErr] = useState(false);
  const [vehicleType, setVehicleType] = useState<string | number>('');
  const handleChangeOtherModel = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherModel(event.target.value);
  };
  const getVehicleForBrandAndSubType = async (brand: number | string, subtype: number | string) => {
    const response = await apiClient.post('/vehicles/brandSubType', {
      brandId: brand,
      subtypeId: subtype,
    });
    const data = await response;
    setModelValueList([
      ...(data.data.data as CarModelList),
      { id: 0, name: 'Иное', car_params: [], value: 'other' },
    ]);
  };
  const getVehicleType = async (model: number | string) => {
    const response = await apiClient.post('/vehicles/give-type', { modelId: model });
    const data = await response;
    setVehicleType(data.data.data);
  };
  const handleChangeModel = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as number;
    const model = modelValueList.find((item) => item.id === value);
    setModel(value);
    setCar_modelErr(false);

    if (model?.value === 'other') {
      showCarModelOtherBlock(true);
    } else {
      setOtherModel('');
      showCarModelOtherBlock(false);

      getVehicleParams(value);
      getVehicleSubtype(value);
      getVehicleType(value);
    }

    // resetAllValues();
  };
  const CarModelOtherBlock = () => {
    return (
      <Box className={`${styles.payment} ${styles.big}`}>
        <SelectInput
          disabled={!brand}
          value={model}
          required
          error={car_modelErr}
          onChange={handleChangeModel}
          placeholder={'Выберите модель'}
        >
          {modelValueList.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const handleChangeKp = (event: any) => {
    setKp(event.target.files);
  };

  const conditionBrandDisable = () => {
    let condition: boolean;
    if (!car_type) {
      condition = true;
    } else {
      condition = false;
    }
    return condition;
  };

  async function handleClickOpen() {
    const formData = new FormData();

    const priceInput = document.querySelector<HTMLInputElement>('[name="price"]');
    const initialInput = document.querySelector<HTMLInputElement>('[name="initial_payment"]');
    const leasePeriodInput = document.querySelector<HTMLInputElement>('[name="lease_period"]');

    formData.append('type', 'leasing');

    if (isDashboard) {
      if (isAgent) {
        formData.append('agent_bio', agentBio ? agentBio?.toString() : '-');
        formData.append('agent_email', agentEmail ? agentEmail?.toString() : '-');
        formData.append('agent_phone', agentPhone ? agentPhone?.toString() : '-');
        formData.append('agent_inn', agentInn ? agentInn?.toString() : '-');
        // formData.append('agent_reward', agentReward?.toString());
      }
    }

    if (!check || checkErr) {
      setCheckErr(true);
    }

    if (email?.toString().length && ValidationRegular.MAIL.test(email?.toString())) {
      formData.append('email', email?.toString() || '');
    } else {
      setEmailErr(true);
    }

    if (phone?.toString().length) {
      formData.append('phone', phone?.toString() || '');
    } else {
      setPhoneErr(true);
    }

    if (bio?.toString().length && ValidationRegular.BIO.test(bio?.toString())) {
      formData.append('bio', bio?.toString() || '');
    } else {
      setBioErr(true);
    }

    if (validateINN(inn?.toString())) {
      formData.append('inn', inn?.toString() || '');
      setInnErr(false);
    } else {
      setInnErr(true);
    }

    if (quantity?.toString().length && ValidationRegular.QUANTITY.test(quantity?.toString())) {
      formData.append('quantity', quantity?.toString() || '');
    } else {
      setQuantityErr(true);
    }

    // if (revenue?.toString().length) {
    //   formData.append('revenue', revenue?.toString() || '');
    // }

    if (revenue?.toString().length) {
      if (ValidationRegular.REVENUE.test(revenue?.toString()) || revenue == '') {
        formData.append('revenue', revenue?.toString() || '');
      } else {
        setRevenueErr(true);
      }
    }

    if (priceInput && priceInput.value) {
      formData.append('price', priceInput.value);
    } else {
      setPriceErr(true);
    }
    if (initialInput && initialInput.value) {
      formData.append('initial_payment', initialInput.value);
    } else {
      setInitialErr(true);
    }
    if (leasePeriodInput && leasePeriodInput.value) {
      formData.append('lease_period', leasePeriodInput.value);
    } else {
      setLeasePeriodErr(true);
    }

    if (currency?.toString().trim()) {
      formData.append('currency', currency.toString().trim());
      setCurrencyErr(false);
    } else {
      setCurrencyErr(true);
    }

    if (carTypeOtherBlock) {
      if (otherCarType) {
        formData.append('car_type_other', otherCarType);
        formData.append('car_subtype_other', '-');
        formData.append('car_brand_other', '-');
        formData.append('car_model_other', '-');

        setCarTypeOtherBlockErr(false);
        setCar_typeErr(false);
        setCar_subtypeErr(false);
        setCar_brandErr(false);
        setCar_modelErr(false);
      } else {
        setCarTypeOtherBlockErr(true);
      }
    } else {
      if (car_type.toString().length) {
        formData.append('car_type', car_type.toString());
      } else {
        setCar_typeErr(true);
      }
    }
    if (carSubtypeOtherBlock) {
      if (otherSubType) {
        formData.append('car_subtype_other', otherSubType);
        formData.append('car_brand_other', '-');
        formData.append('car_model_other', '-');

        setCarSubtypeOtherBlockErr(false);
        setCar_subtypeErr(false);
        setCar_brandErr(false);
        setCar_modelErr(false);
      } else {
        setCarSubtypeOtherBlockErr(true);
      }
    } else {
      if (car_subtype.toString()) {
        formData.append('car_subtype', car_subtype.toString());
      } else if (subtypeMain) {
        formData.append('car_subtype', subtypeMain.toString());
      } else {
        setCar_subtypeErr(true);
      }
    }

    if (carBrandOtherBlock) {
      if (otherBrand) {
        formData.append('car_brand_other', otherBrand);
        formData.append('car_model_other', '-');
        setCarBrandOtherBlockErr(false);
        setCar_brandErr(false);
        setCar_modelErr(false);
      } else {
        setCarBrandOtherBlockErr(true);
      }
    } else {
      if (brand.toString().length) {
        formData.append('car_brand', brand.toString());
      } else {
        setCar_brandErr(true);
      }
      if (model.toString().length) {
        formData.append('vehicle', model.toString());
      } else {
        setCar_modelErr(true);
      }
    }

    if (carModelOtherBlock) {
      if (otherModel) {
        formData.append('car_model_other', otherModel);

        setCarModelOtherBlockErr(false);
        setCar_modelErr(false);
      } else {
        setCarModelOtherBlockErr(true);
      }
    } else {
      if (model.toString()) {
        formData.append('vehicle', model.toString());
      } else {
        setCar_modelErr(true);
      }
    }

    if (kp && kp[0]) {
      formData.append('kp', kp[0]);
    }

    if (paymentTypeOtherBlock) {
      if (paymentTypeOther) {
        formData.append('payment_type', paymentTypeOther);
      } else {
        setPaymentTypeErr(true);
      }
    } else {
      if (paymentType) {
        const value = paymentTypesList.find((item) => item.value === paymentType);
        formData.append('payment_type', value?.name || paymentType);
      } else {
        setPaymentTypeErr(true);
      }
    }

    const typeError = carTypeOtherBlock ? carTypeOtherBlockErr : car_typeErr;
    const subtypeError =
      !carTypeOtherBlock && (carSubtypeOtherBlock ? carSubtypeOtherBlockErr : car_subtypeErr);
    const brandError =
      !carTypeOtherBlock &&
      !carSubtypeOtherBlock &&
      (carBrandOtherBlock ? carBrandOtherBlockErr : car_brandErr);
    const modelError =
      !carTypeOtherBlock &&
      !carSubtypeOtherBlock &&
      !carBrandOtherBlock &&
      (carModelOtherBlock ? carModelOtherBlockErr : car_modelErr);

    const validation = !!(
      !bioErr &&
      !emailErr &&
      !phoneErr &&
      !quantityErr &&
      !innErr &&
      !currencyErr &&
      quantity &&
      currency?.toString().trim() &&
      !typeError &&
      !subtypeError &&
      !brandError &&
      !modelError &&
      !paymentTypeErr &&
      !checkErr
    );

    // console.log(validation);

    // Добавляем параметр fulfilled для обозначения завершенной заявки
    formData.append('fulfilled', 'true');

    const referal = searchParams.get('referal');
    if (referal) {
      const response = await apiClient.get(`/agents/checkReferal?id=${referal}`);
      const data = response?.data?.data;

      if (!data?.exists) {
        console.log('Пользователь с таким реферальным кодом не найден');
      } else {
        formData.append('referal_id', referal);
      }
    }

    if (validation) {
      try {
        setLoading(true);

        const url =
          user?.user_type === 'client' || isDashboard
            ? '/leasing-applications/new'
            : '/new-user-app';

        await apiClient.post(url, formData);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        window.ym && window.ym?.(105273634, 'reachGoal', 'submit_leasing_form_success');

        // Очищаем автосохраненные данные при успешной отправке
        clearAutoSavedData();

        if (isDashboard) {
          navigate(-1);
        } else {
          setModalTitle('Благодарим за оформление заявки!');
          setModalDesc(
            'На Ваш E-mail отправлено письмо, содержащее в себе логин и пароль для личного кабинета. В нем будут храниться все ваши заявки.',
          );
          setTitleModalBtn('Войти в личный кабинет');
          setUrl('/auth');
          setOpen(true);
        }
      } catch (error: any) {
        console.log(error);
        if (error.response?.data?.error?.errors) {
          // Карта для перевода полей на русский язык
          const fieldTranslations: Record<string, string> = {
            email: 'E-mail',
            phone: 'Телефон',
            inn: 'ИНН',
            bio: 'ФИО',
            quantity: 'Количество ТС',
            revenue: 'Выручка',
            car_type: 'Тип ТС',
            car_subtype: 'Подтип ТС',
            car_brand: 'Марка',
            car_model: 'Модель',
            payment_type: 'График платежей',
            currency: 'Валюта',
          };
          const errorFields: string[] = [];
          const errorUniqueFields: string[] = [];
          error.response.data.error.errors.forEach((err: any) => {
            if (Array.isArray(err.path)) {
              err.path.forEach((field: string) => {
                switch (field) {
                  case 'email':
                    setEmailErr(true);
                    break;
                  case 'phone':
                    setPhoneErr(true);
                    break;
                  case 'inn':
                    setInnErr(true);
                    break;
                  case 'bio':
                    setBioErr(true);
                    break;
                  case 'quantity':
                    setQuantityErr(true);
                    break;
                  case 'revenue':
                    setRevenueErr(true);
                    break;
                  case 'car_type':
                    setCar_typeErr(true);
                    break;
                  case 'car_subtype':
                    setCar_subtypeErr(true);
                    break;
                  case 'car_brand':
                    setCar_brandErr(true);
                    break;
                  case 'car_model':
                    setCar_modelErr(true);
                    break;
                  case 'payment_type':
                    setPaymentTypeErr(true);
                    break;
                  case 'currency':
                    setCurrencyErr(true);
                    break;
                }
                const label = fieldTranslations[field] ? fieldTranslations[field] : field;

                if (err.message.includes('unique')) {
                  errorUniqueFields.push(label);
                } else {
                  errorFields.push(label);
                }
              });
            }
          });
          setModalTitle('Ошибка!');
          const uniqueText =
            errorUniqueFields.length > 0
              ? `Следующие поля должны быть уникальными: ${errorUniqueFields.join(', ')}.`
              : '';
          const errorText =
            errorFields.length > 0
              ? `Проверьте корректность заполнения следующих полей: ${errorFields.join(', ')}.`
              : '';
          const text = `${uniqueText} ${errorText}`.trim();
          if (text) {
            setModalDesc(text);
          } else {
            setModalDesc('Проверьте корректность заполнения полей.');
          }
          setTitleModalBtn('Продолжить');
          setUrl('');
        } else if (error.response?.data?.error?.message || error.response.data?.error) {
          const errorMessage = error.response.data?.error?.message || error.response.data?.error;

          if (errorMessage === 'User already exists') {
            setModalTitle('Ошибка! Вы уже зарегистрированы!');
            setModalDesc(
              'Создать новую или узнать статус текущих заявок можно в личном кабинете TLS Group.',
            );
            setTitleModalBtn('Войти в личный кабинет');
            setUrl('/auth');
          } else if (errorMessage === 'Missing required fields') {
            setModalTitle('Ошибка! Проверьте заполнение обязательных полей!');
            setModalDesc('Для оформления заявки должны быть заполнены все обязательные поля!');
            setTitleModalBtn('Продолжить');
            setUrl('');
          } else {
            setModalTitle('Ошибка!');
            setModalDesc('Произошла непредвиденная ошибка. Попробуйте снова позже.');
            setTitleModalBtn('Продолжить');
            setUrl('');
          }
        } else {
          setModalTitle('Ошибка!');
          setModalDesc('Произошла непредвиденная ошибка. Попробуйте снова позже.');
          setTitleModalBtn('Продолжить');
          setUrl('');
        }
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }
  }

  type PaymentType = {
    name: string;
    value: string;
  };

  const paymentTypesList: PaymentType[] = [
    { name: 'Аннуитетный', value: 'annuity' },
    { name: 'Дифференцированный', value: 'differential' },
    { name: 'Сезонный', value: 'seasonal' },
    { name: 'Индивидуальный', value: 'individual' },
  ];

  const [paymentType, setPaymentType] = useState<PaymentType['value']>('annuity');
  const [paymentTypeErr, setPaymentTypeErr] = useState<boolean>(false);
  const [paymentTypeOtherBlock, setPaymentTypeOtherBlock] = useState<boolean>(false);
  const [paymentTypeOther, setPaymentTypeOther] = useState<string>('');

  const handleChangePaymentType = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as PaymentType['value'];
    setPaymentType(value);
    if (value === 'individual' || value === 'seasonal') {
      setPaymentTypeOtherBlock(true);
    } else {
      setPaymentTypeOtherBlock(false);
    }
  };

  const PaymentTypeBlock = () => {
    return (
      <Box className={`${styles.ts} ${styles.big}`}>
        <SelectInput
          name={'payment_type'}
          value={paymentType}
          required
          onChange={handleChangePaymentType}
          placeholder={'График платежей'}
        >
          {paymentTypesList?.map((item: PaymentType) => (
            <MenuItem key={item.value} value={item.value}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const handleChangePaymentTypeOther = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentTypeOther(event.target.value);
  };

  // Собираем все данные формы для автосохранения
  const formData = useMemo(() => {
    // Получаем значения слайдеров
    const priceInput = document.querySelector<HTMLInputElement>('[name="price"]');
    const initialInput = document.querySelector<HTMLInputElement>('[name="initial_payment"]');
    const leasePeriodInput = document.querySelector<HTMLInputElement>('[name="lease_period"]');

    return {
      bio,
      email,
      phone,
      inn,
      quantity,
      revenue,
      car_type: carTypeOtherBlock ? otherCarType : car_type,
      car_subtype: carSubtypeOtherBlock ? otherSubType : car_subtype,
      car_brand: carBrandOtherBlock ? otherBrand : brand,
      vehicle: carModelOtherBlock ? otherModel : model,
      car_type_other: carTypeOtherBlock ? otherCarType : undefined,
      car_subtype_other: carSubtypeOtherBlock ? otherSubType : undefined,
      car_brand_other: carBrandOtherBlock ? otherBrand : undefined,
      car_model_other: carModelOtherBlock ? otherModel : undefined,
      price: priceInput?.value || undefined,
      initial_payment: initialInput?.value || undefined,
      lease_period: leasePeriodInput?.value || undefined,
      payment_type: paymentTypeOtherBlock ? paymentTypeOther : paymentType,
      payment_type_other: paymentTypeOtherBlock ? paymentTypeOther : undefined,
      currency: currency?.toString().trim() || undefined,
    };
  }, [
    bio,
    email,
    phone,
    inn,
    quantity,
    revenue,
    car_type,
    car_subtype,
    brand,
    model,
    carTypeOtherBlock,
    otherCarType,
    carSubtypeOtherBlock,
    otherSubType,
    carBrandOtherBlock,
    otherBrand,
    carModelOtherBlock,
    otherModel,
    paymentType,
    paymentTypeOtherBlock,
    paymentTypeOther,
    currency,
  ]);

  // Используем хук автосохранения
  const { getStoredData, clearAutoSavedData } = useFormAutoSave({
    formId: 'main-leasing-calc',
    data: formData,
    enabled: !!phone && phone.length > 10 && !phoneErr,
    saveInterval: 2000,
    formType: 'leasing',
  });

  return (
    <Box component='form' className={styles.calcContainer}>
      <Box className={styles.title}>{title}</Box>
      <Box className={`${styles.tel} ${styles.big}`}>
        <PhoneInput
          onChange={handleChangePhone}
          value={phone}
          error={phoneErr}
          name={'phone'}
          required
          placeholder={'Введите телефон'}
        />
      </Box>
      <Box className={`${styles.inn} ${styles.big}`}>
        <TextInput
          onChange={handleChangeInn}
          value={inn}
          error={innErr}
          name={'inn'}
          type={'number'}
          placeholder={'введите ИНН покупателя/лизингополучателя'}
          required
        />
      </Box>
      {(!isDashboard || (isDashboard && isAgent)) && (
        <>
          <Box className={`${styles.email} ${styles.big}`}>
            <TextInput
              onChange={handleChangeEmail}
              value={email}
              error={emailErr}
              name={'email'}
              type={'email'}
              required
              placeholder={'Введите E-mail'}
            />
          </Box>
          <Box className={`${styles.bio} ${styles.big}`}>
            <TextInput
              onChange={handleChangeBio}
              value={bio}
              error={bioErr}
              name={'bio'}
              type={'text'}
              placeholder={'введите ФИО представителя покупателя/лизингополучателя'}
              required
            />
          </Box>
        </>
      )}
      <Collapse
        in={hasRequiredFields}
        timeout={600}
        unmountOnExit
        sx={{
          '& .MuiCollapse-wrapper': {
            width: '100%',
          },
          '& .MuiCollapse-wrapperInner': {
            width: '100%',
          },
        }}
      >
        <Box className={styles.calcContainer}>
          <Box className={`${styles.quantity} ${styles.small}`}>
            <TextInput
              onChange={handleChangeQuantity}
              value={quantity}
              error={quantityErr}
              required
              name={'quantity'}
              type={'number'}
              placeholder={'Введите кол-во ТС'}
            />
          </Box>

          <Box className={`${styles.payment} ${styles.big}`}>
            <SelectInput
              name={'currency'}
              value={currency}
              onChange={handleChangeCurrency}
              required
              placeholder={'Выберите валюту'}
              error={currencyErr}
            >
              <MenuItem value='Рубль'>Рубль</MenuItem>
              <MenuItem value='Доллар'>Доллар</MenuItem>
              <MenuItem value='Евро'>Евро</MenuItem>
              <MenuItem value='Юань'>Юань</MenuItem>
            </SelectInput>
          </Box>

          <CarTypeBlock />
          {carTypeOtherBlock ? (
            <Box className={`${styles.bio} ${styles.big}`}>
              <TextInput
                name={'otherCarType'}
                type={'text'}
                onChange={handleChangeOtherCarType}
                value={otherCarType || ''}
                placeholder={'Введите свой тип ТС'}
                required
                error={carTypeOtherBlockErr}
              />
            </Box>
          ) : (
            <>
              <CarSubTypeBlock />
              {carSubtypeOtherBlock ? (
                <Box className={`${styles.bio} ${styles.big}`}>
                  <TextInput
                    name={'otherSubType'}
                    type={'text'}
                    onChange={handleChangeOtherSubType}
                    value={otherSubType || ''}
                    placeholder={'Введите свой подтип ТС'}
                    required
                    error={carSubtypeOtherBlockErr}
                  />
                </Box>
              ) : (
                <>
                  <CarBrandOtherBlock />
                  {carBrandOtherBlock ? (
                    <Box className={`${styles.bio} ${styles.big}`}>
                      <TextInput
                        name={'otherBrand'}
                        type={'text'}
                        onChange={handleChangeOtherBrand}
                        value={otherBrand || ''}
                        placeholder={'Введите свою марку'}
                        required
                        error={carBrandOtherBlockErr}
                      />
                    </Box>
                  ) : (
                    <>
                      <CarModelOtherBlock />
                      {carModelOtherBlock && (
                        <Box className={`${styles.bio} ${styles.big}`}>
                          <TextInput
                            name={'otherModel'}
                            type={'text'}
                            onChange={handleChangeOtherModel}
                            value={otherModel || ''}
                            placeholder={'Введите свою модель'}
                            required
                            error={carModelOtherBlockErr}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          <Box className={`${styles.revenue} ${styles.big}`}>
            <TextInput
              name={'revenue'}
              onChange={handleChangeRevenue}
              error={revenueErr}
              value={revenue}
              type={'number'}
              placeholder={'Введите выручку в год'}
            />
          </Box>
          <Box className={`${styles.slider} ${styles.big}`}>
            <SliderInput
              name='price'
              title={'стоимость 1 ед. предмета лизинга, ₽'}
              marks={marksPrice}
              step={100000}
              defaultValue={5000000}
              min={1000000}
              max={50000000}
              symbol={'₽'}
            />
          </Box>
          <Box className={`${styles.slider} ${styles.big}`}>
            <SliderInput
              name='initial_payment'
              title={'Выберите первоначальный взнос, %'}
              marks={marksInitialPayment}
              step={0.1}
              defaultValue={10}
              min={0}
              max={49}
              symbol={'%'}
            />
          </Box>
          <Box className={`${styles.slider} ${styles.big}`}>
            <SliderInput
              name='lease_period'
              title={'Выберите срок лизинга, мес.'}
              marks={marksTerm}
              step={0.5}
              defaultValue={18}
              min={12}
              max={84}
              symbol={'мес'}
            />
          </Box>

          <PaymentTypeBlock />
          {paymentTypeOtherBlock && (
            <Box className={`${styles.bio} ${styles.big}`}>
              <TextInput
                name={'payment_type_other'}
                type={'text'}
                placeholder={'Введите свой график платежей'}
                required
                error={paymentTypeErr}
                onChange={handleChangePaymentTypeOther}
              />
            </Box>
          )}
        </Box>
      </Collapse>

      <Box className={`${styles.accept} ${styles.big}`}>
        <FormControlLabel
          sx={{
            margin: 0,
            '.MuiFormControlLabel-label': {
              fontSize: '16px',
              fontWeight: 500,
              letterSpacing: '0.02em',
              color: '#96A8B8',
            },
          }}
          control={
            <CheckboxInput
              className={clsx([checkErr && `${styles.accept_err}`])}
              name='remember'
              checked={check}
              onChange={handleChangeCheck}
            />
          }
          label={
            <div className={styles.grayRules}>
              <span>Я согласен с</span>{' '}
              <span className={styles.blueRules} onClick={() => rules(event)}>
                политикой обработки
              </span>
              <span className={styles.blueRules} onClick={() => rules(event)}>
                персональных данных
              </span>
            </div>
          }
        />
      </Box>
      <Box className={`${styles.file} ${styles.big}`}>
        <FileInputCustom
          value={kp}
          onChange={handleChangeKp}
          text={'Прикрепить файл с имеющимся предложением'}
        />
      </Box>
      <Box className={`${styles.submitBtn} ${styles.big}`}>
        <ButtonCustom onClick={handleClickOpen} disabled={loading}>
          {loading ? 'Подождите...' : 'Оформить заявку на лизинг'}
        </ButtonCustom>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title-leasing'
        aria-describedby='alert-dialog-description-leasing'
        sx={{
          '.MuiDialog-paper': {
            padding: '40px 40px 50px',
            borderRadius: '10px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='alert-dialog-title-leasing'>
          <span>{modalTitle}</span>
          <IconButton
            disableRipple
            aria-label='close'
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              fontSize: 18,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            className={styles.modalDescription}
            id='alert-dialog-description-leasing'
          >
            <span>{modalDesc}</span>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          {!isDashboard && (
            <ButtonCustom
              className={styles.modalButton}
              onClick={() => {
                navigate(`${url}`);
                setOpen(false);
              }}
            >
              {titleModalBtn}
            </ButtonCustom>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
